// Copyright 2017 Sorama B.V.

import { bindable, BindingEngine, Disposable, inject } from "aurelia-framework";
import { SelectableListBase } from "controls/selectable-list-base/SelectableListBase";
import { IFilter } from "filters/IFilter";
import { IGenericRepository } from "repositories/IGenericRepository";
import { MeasurementSetupRepository } from "repositories/MeasurementSetupRepository";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { MeasurementDeviceSetup, MeasurementDeviceSetupInfo } from "services/Hardware/Datatypes/MeasurementDeviceSetup";

@inject(Element, MeasurementSetupRepository, "IGenericRepository<MeasurementDevice,MeasurementDevice>", BindingEngine)
export class MeasurementSetupList extends SelectableListBase<MeasurementDeviceSetup, MeasurementDeviceSetupInfo> {
    //#region Injected Services
    private measurementSetupRepository: MeasurementSetupRepository;
    private measurementDeviceRepository: IGenericRepository<MeasurementDevice, MeasurementDevice>;
    //#endregion

    //#region SelectableListBase Properties
    @bindable
    public selectedItemId: string;

    @bindable
    public selectedChildItemId: string;

    @bindable
    public checkedItemIds: string[] = [];

    @bindable
    public filter: IFilter<MeasurementDeviceSetup>;

    @bindable
    public canAdd: boolean;
    //#endregion

    //#region Private Properties
    private subscriptions: Disposable[];
    //#endregion

    constructor(
        element: Element,
        measurementSetupRepository: MeasurementSetupRepository,
        measurementDeviceRepository: IGenericRepository<MeasurementDevice, MeasurementDevice>,
        bindingEngine: BindingEngine
    ) {
        super(element, bindingEngine);

        this.measurementDeviceRepository = measurementDeviceRepository;
        this.measurementSetupRepository = measurementSetupRepository;
        this.refresh();
    }

    public bind(): void {
        super.bind();

        this.subscriptions = [
            this.bindingEngine
                .collectionObserver(this.measurementSetupRepository.items)
                .subscribe(() => this.refresh()),
        ];

        this.refresh();
    }

    public unbind(): void {
        while (this.subscriptions.length !== 0) {
            this.subscriptions.pop().dispose();
        }

        super.unbind();
    }

    //#region SelectableListBase Members
    protected getItems(): Promise<MeasurementDeviceSetup[]> {
        return this.measurementSetupRepository.getAll().then((setups) => {
            return Promise.all(
                setups.map((setup) => MeasurementDeviceSetup.fromRepository(setup, this.measurementDeviceRepository))
            );
        });
    }

    protected getChildItemsFromItem(item: MeasurementDeviceSetup): Promise<MeasurementDeviceSetupInfo[]> {
        return Promise.resolve(item);
    }

    protected deleteItem(itemId: string): Promise<void> {
        return this.measurementSetupRepository.delete(itemId).then(() => {
            if (itemId === this.selectedItemId) {
                this.selectedItemId = null;
                this.selectedChildItemId = null;
            }
        });
    }

    protected deleteChildItem(childItemId: string, itemId: string): Promise<void> {
        return this.measurementSetupRepository
            .getById(itemId)
            .then((setup) => {
                const infoIdx = setup.measurementDevices.findIndex((x) => x.id === childItemId);

                if (infoIdx !== -1) {
                    setup.measurementDevices.splice(infoIdx, 1);
                    return this.measurementSetupRepository.put(setup);
                }
            })
            .then(() => {
                if (childItemId === this.selectedChildItemId) {
                    this.selectedChildItemId = null;
                }
            });
    }
    //#endregion
}
